import React, { FC, useContext, useEffect, useRef } from 'react'
import { Box, Typography } from '@mui/material'
import { useDrag, useDrop } from 'react-dnd'

import { Checkbox } from '@/components/checkbox.tsx'
import { Icon } from '@/components'
import { DndCustomContext } from './dnd-custom-context.tsx'

export type RowProps = {
  id: string
  label: string
  isChecked: boolean
}

type DraggableRowProps = RowProps & {
  index: number
  moveRow: (dragIndex: number, hoverIndex: number) => void
  translateY: number
  onCheckChange: (id: string, isCheck: boolean) => void
}

export const DraggableRow: FC<DraggableRowProps> = ({
  id,
  label,
  index,
  moveRow,
  translateY,
  isChecked,
  onCheckChange,
}) => {
  const ref = useRef<HTMLDivElement>(null)
  const { isDragging, setIsDragging, isTouchDevice } = useContext(DndCustomContext)

  const [{ isDragging: isDraggingSource }, drag] = useDrag({
    type: 'row',
    item: { id, index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  })

  useEffect(() => {
    setIsDragging(isDraggingSource)
  }, [isDraggingSource, setIsDragging])

  const [, drop] = useDrop({
    accept: 'row',
    hover: (item: { id: string; index: number }, monitor) => {
      if (!ref.current) return

      const dragId = item.id
      const hoverId = id

      if (dragId === hoverId) return

      const dragIndex = item.index
      const hoverIndex = index

      const hoverBoundingRect = ref.current.getBoundingClientRect()
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2
      const clientOffset = monitor.getClientOffset()
      const hoverClientY = clientOffset?.y || 0

      const isDraggingUp = dragIndex < hoverIndex
      const isHoveringPastMiddle = hoverClientY > hoverBoundingRect.top + hoverMiddleY

      if (isDraggingUp && !isHoveringPastMiddle) return
      if (!isDraggingUp && isHoveringPastMiddle) return

      moveRow(dragIndex, hoverIndex)

      item.index = hoverIndex
    },
  })

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onCheckChange(id, event.currentTarget.checked)
  }

  drag(drop(ref))

  return (
    <Box
      ref={ref}
      draggable={true}
      sx={(theme) => ({
        opacity: isDraggingSource && !isTouchDevice ? 0.2 : 1,
        transform: `translateY(${translateY}px)`,
        borderRadius: '8px',
        padding: '2px 10px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor:
          isDraggingSource && isTouchDevice ? theme.palette.background.blue : theme.palette.secondary.main,
        cursor: 'grab',
        transition: isDragging ? '0.2s' : '0s',
      })}
    >
      <Checkbox value={isChecked} onChange={onChange} />
      <Typography variant="body2" color="text.main" flex={1}>
        {label}
      </Typography>
      <Icon name="DragPlace" size="lg" />
    </Box>
  )
}
